import { useState, useEffect } from "react";

type ScreenSize = "mobile" | "tablet" | "desktop" | "large";

interface ResponsiveValues {
    cardPadding: string;
    titleSize: string;
    descriptionSize: string;
    iconSize: { main: string; lightbulb: string; check: string; info: string };
    iconBoxSize: string;
    checkmarkSize: string;
    gap: string;
    marginBottom: string;
    previewBadge: string;
    samplePromptsSize: string;
    buttonHeight: string;
    buttonFontSize: string;
    borderRadius: string;
    infoBtnSize: string;
    mainHeaderSize: string;
    mainSubtitleSize: string;
    groupHeaderSize: string;
    groupSubtitleSize: string;
    groupIconSize: string;
    startButtonSize: string;
    startButtonHeight: string;
    startButtonPadding: string;
    accordionPadding: string;
}

const responsiveValues: Record<ScreenSize, ResponsiveValues> = {
    mobile: {
        cardPadding: "14px",
        titleSize: "14px",
        descriptionSize: "11px",
        iconSize: { main: "24px", lightbulb: "13px", check: "11px", info: "11px" },
        iconBoxSize: "24px",
        checkmarkSize: "22px",
        gap: "8px",
        marginBottom: "10px",
        previewBadge: "8px",
        samplePromptsSize: "10px",
        buttonHeight: "22px",
        buttonFontSize: "10px",
        borderRadius: "12px",
        infoBtnSize: "26px",
        mainHeaderSize: "24px",
        mainSubtitleSize: "14px",
        groupHeaderSize: "16px",
        groupSubtitleSize: "11px",
        groupIconSize: "28px",
        startButtonSize: "15px",
        startButtonHeight: "48px",
        startButtonPadding: "14px 40px",
        accordionPadding: "16px 20px"
    },
    tablet: {
        cardPadding: "16px",
        titleSize: "15px",
        descriptionSize: "12px",
        iconSize: { main: "28px", lightbulb: "14px", check: "12px", info: "12px" },
        iconBoxSize: "28px",
        checkmarkSize: "24px",
        gap: "9px",
        marginBottom: "11px",
        previewBadge: "9px",
        samplePromptsSize: "11px",
        buttonHeight: "24px",
        buttonFontSize: "10px",
        borderRadius: "14px",
        infoBtnSize: "28px",
        mainHeaderSize: "28px",
        mainSubtitleSize: "16px",
        groupHeaderSize: "17px",
        groupSubtitleSize: "12px",
        groupIconSize: "32px",
        startButtonSize: "16px",
        startButtonHeight: "52px",
        startButtonPadding: "16px 46px",
        accordionPadding: "18px 22px"
    },
    desktop: {
        cardPadding: "18px",
        titleSize: "16px",
        descriptionSize: "12px",
        iconSize: { main: "30px", lightbulb: "15px", check: "13px", info: "13px" },
        iconBoxSize: "30px",
        checkmarkSize: "26px",
        gap: "10px",
        marginBottom: "12px",
        previewBadge: "9px",
        samplePromptsSize: "11px",
        buttonHeight: "26px",
        buttonFontSize: "11px",
        borderRadius: "16px",
        infoBtnSize: "30px",
        mainHeaderSize: "30px",
        mainSubtitleSize: "17px",
        groupHeaderSize: "18px",
        groupSubtitleSize: "12px",
        groupIconSize: "34px",
        startButtonSize: "16px",
        startButtonHeight: "54px",
        startButtonPadding: "17px 48px",
        accordionPadding: "19px 23px"
    },
    large: {
        cardPadding: "20px",
        titleSize: "17px",
        descriptionSize: "13px",
        iconSize: { main: "32px", lightbulb: "16px", check: "14px", info: "14px" },
        iconBoxSize: "32px",
        checkmarkSize: "28px",
        gap: "10px",
        marginBottom: "12px",
        previewBadge: "9px",
        samplePromptsSize: "12px",
        buttonHeight: "26px",
        buttonFontSize: "11px",
        borderRadius: "16px",
        infoBtnSize: "32px",
        mainHeaderSize: "32px",
        mainSubtitleSize: "18px",
        groupHeaderSize: "19px",
        groupSubtitleSize: "13px",
        groupIconSize: "36px",
        startButtonSize: "17px",
        startButtonHeight: "56px",
        startButtonPadding: "18px 52px",
        accordionPadding: "20px 24px"
    }
};

const getScreenSize = (): ScreenSize => {
    if (typeof window === "undefined") return "desktop";
    const width = window.innerWidth;
    if (width < 1024) return "mobile";
    if (width < 1440) return "tablet";
    if (width < 1920) return "desktop";
    return "large";
};

export const useResponsive = () => {
    const [screenSize, setScreenSize] = useState<ScreenSize>(getScreenSize());

    useEffect(() => {
        const handleResize = () => {
            setScreenSize(getScreenSize());
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return {
        screenSize,
        size: responsiveValues[screenSize]
    };
};
